/**
 * Storage Migration Service
 * 
 * Handles migration from old localStorage/sessionStorage formats to new formats.
 * Ensures backward compatibility when users upgrade to new app version.
 * 
 * Issue: #202
 */

import { Injectable } from '@angular/core'
import { Store } from '@/domain/store'
import { ServiceProduct } from '@/domain/service-product'
import { ServiceProductVariation } from '@/domain/service-product-variation'
import { log, warn, error } from '@/util/logger'

/**
 * Service for migrating old storage formats to new formats.
 * Ensures users upgrading from old versions don't lose their data.
 */
@Injectable({ providedIn: 'root' })
export class StorageMigrationService {
  private readonly MIGRATION_VERSION_KEY = 'storage_migration_version'
  private readonly CURRENT_MIGRATION_VERSION = 2

  /**
   * Check if migration is needed and perform it
   */
  migrateIfNeeded(): void {
    const lastVersion = this._getMigrationVersion()
    
    log('[StorageMigration] Checking migration status', {
      currentVersion: this.CURRENT_MIGRATION_VERSION,
      lastVersion,
      needsMigration: lastVersion < this.CURRENT_MIGRATION_VERSION
    })
    
    if (lastVersion < this.CURRENT_MIGRATION_VERSION) {
      log(`[StorageMigration] Migrating from version ${lastVersion} to ${this.CURRENT_MIGRATION_VERSION}`)
      
      try {
        const migrationStart = Date.now()
        this._migrateSessionStorage()
        this._migrateLocalStorage()
        
        // Mark migration as complete
        this._setMigrationVersion(this.CURRENT_MIGRATION_VERSION)
        const duration = Date.now() - migrationStart
        log('[StorageMigration] Migration completed successfully', {
          duration: `${duration}ms`
        })
      } catch (err) {
        error('[StorageMigration] Migration failed', {
          error: err
        })
        // Don't throw - allow app to continue with defaults
      }
    } else {
      log('[StorageMigration] No migration needed, already at version', lastVersion)
    }
    
    // After migration check, clean up legacy keys if user is on new architecture
    this._cleanupLegacyKeys()
  }

  /**
   * Migrate old sessionStorage keys to new UIStateService format
   */
  private _migrateSessionStorage(): void {
    const uiStateKey = 'deux_ui'
    
    // Check if new format already exists
    if (sessionStorage.getItem(uiStateKey)) {
      log('[StorageMigration] New UI state format already exists, skipping migration')
      return
    }

    // Migrate old sessionStorage keys to new format
    const migratedState: any = {
      posSelectedStore: null,
      posSelectedProducts: [],
      calendarSelectedStore: null,
      calendarSelectedProduct: null,
      calendarSelectedVariation: null
    }

    // Migrate POS store selection
    const posStoreId = sessionStorage.getItem('pos.storeId')
    if (posStoreId && !isNaN(+posStoreId)) {
      migratedState.posSelectedStore = { id: +posStoreId }
      log('[StorageMigration] Migrated pos.storeId:', posStoreId)
    }

    // Migrate Calendar selections
    const calendarStoreId = sessionStorage.getItem('calendar.store_id')
    if (calendarStoreId && !isNaN(+calendarStoreId)) {
      migratedState.calendarSelectedStore = { id: +calendarStoreId }
      log('[StorageMigration] Migrated calendar.store_id:', calendarStoreId)
    }

    const calendarProductId = sessionStorage.getItem('calendar.product_id')
    if (calendarProductId && !isNaN(+calendarProductId)) {
      migratedState.calendarSelectedProduct = { id: +calendarProductId }
      log('[StorageMigration] Migrated calendar.product_id:', calendarProductId)
    }

    const calendarVariationId = sessionStorage.getItem('calendar.variation_id')
    if (calendarVariationId && !isNaN(+calendarVariationId)) {
      migratedState.calendarSelectedVariation = { id: +calendarVariationId }
      log('[StorageMigration] Migrated calendar.variation_id:', calendarVariationId)
    }

    // Only save if we have something to migrate
    if (posStoreId || calendarStoreId || calendarProductId || calendarVariationId) {
      try {
        sessionStorage.setItem(uiStateKey, JSON.stringify(migratedState))
        log('[StorageMigration] Saved migrated UI state')
      } catch (e) {
        warn('[StorageMigration] Failed to save migrated state', e)
      }
    }
  }

  /**
   * Migrate localStorage data if needed
   */
  private _migrateLocalStorage(): void {
    // Validate and fix currentUser format if needed
    const currentUserStr = localStorage.getItem('currentUser')
    if (currentUserStr) {
      try {
        const user = JSON.parse(currentUserStr)
        
        // Validate required fields
        if (!user || typeof user !== 'object') {
          warn('[StorageMigration] Invalid currentUser format, clearing')
          localStorage.removeItem('currentUser')
          return
        }

        // Ensure refresh_token exists (for legacy sessions)
        // This is handled by _maybeUpgradeLegacySession in AuthenticationService
        // but we can validate the structure here
        
        // Check for corrupted data
        if (user.token && typeof user.token !== 'string') {
          warn('[StorageMigration] Invalid token format in currentUser, clearing')
          localStorage.removeItem('currentUser')
          return
        }

        // If user object is valid, it will be used as-is
        // No migration needed for currentUser format
      } catch (e) {
        error('[StorageMigration] Failed to parse currentUser, clearing corrupted data', e)
        localStorage.removeItem('currentUser')
      }
    }
  }

  /**
   * Get last migration version
   */
  private _getMigrationVersion(): number {
    try {
      const version = localStorage.getItem(this.MIGRATION_VERSION_KEY)
      return version ? parseInt(version, 10) : 0
    } catch (e) {
      warn('[StorageMigration] Failed to read migration version', e)
      return 0
    }
  }

  /**
   * Set migration version
   */
  private _setMigrationVersion(version: number): void {
    try {
      localStorage.setItem(this.MIGRATION_VERSION_KEY, version.toString())
    } catch (e) {
      warn('[StorageMigration] Failed to save migration version', e)
    }
  }

  /**
   * Clean up legacy sessionStorage keys once user has migrated to new architecture.
   * This removes old keys that are no longer needed once deux_ui is being used.
   * Only cleans up keys that have been migrated to deux_ui format.
   */
  private _cleanupLegacyKeys(): void {
    const migrationVersion = this._getMigrationVersion()
    const hasNewFormat = !!sessionStorage.getItem('deux_ui')
    
    // Only clean up if:
    // 1. Migration has been completed (version >= 2)
    // 2. User is using new format (deux_ui exists)
    if (migrationVersion >= this.CURRENT_MIGRATION_VERSION && hasNewFormat) {
      // Only clean up keys that were migrated to deux_ui
      const legacyKeys = [
        'pos.storeId',
        'calendar.store_id',
        'calendar.product_id',
        'calendar.variation_id'
        // Note: dashboard.storeId is not part of deux_ui migration, so we don't clean it up
      ]
      
      let cleanedCount = 0
      legacyKeys.forEach(key => {
        if (sessionStorage.getItem(key)) {
          try {
            sessionStorage.removeItem(key)
            cleanedCount++
            log('[StorageMigration] Removed legacy key:', key)
          } catch (e) {
            warn('[StorageMigration] Failed to remove legacy key', { key, error: e })
          }
        }
      })
      
      if (cleanedCount > 0) {
        log('[StorageMigration] Legacy key cleanup completed', {
          keysRemoved: cleanedCount
        })
      }
    }
  }

  /**
   * Check if legacy keys should still be used (for backward compatibility during transition)
   * Returns true if user hasn't migrated yet or new format doesn't exist
   */
  shouldUseLegacyKeys(): boolean {
    const migrationVersion = this._getMigrationVersion()
    const hasNewFormat = !!sessionStorage.getItem('deux_ui')
    
    // Use legacy keys only if migration hasn't completed or new format doesn't exist
    return migrationVersion < this.CURRENT_MIGRATION_VERSION || !hasNewFormat
  }

  /**
   * Clear all migrated data (for testing/debugging)
   */
  clearMigrationData(): void {
    localStorage.removeItem(this.MIGRATION_VERSION_KEY)
    sessionStorage.removeItem('deux_ui')
  }
}
